import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, type ComponentType } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider } from './context/AppContext';
import { VerificationProvider } from './context/VerificationContext';
import { MainLayout } from './layouts/MainLayout';
import { LanguageSync } from './components/LanguageSync';
import { AdminSaveProvider } from './context/AdminSaveContext';
import { ToastProvider } from './components/ui/Toast';
import { ScrollManager } from './components/ScrollManager';
import { AdminTranslationBoundary } from './components/AdminTranslationBoundary';
import { CAREER_RESOURCE_SEGMENTS, STATIC_ROUTE_SEGMENTS } from './utils/routes';

const Home = lazy(() => import('./pages/Home').then((module) => ({ default: module.Home })));
const Services = lazy(() => import('./pages/Services').then((module) => ({ default: module.Services })));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail').then((module) => ({ default: module.ServiceDetail })));
const Languages = lazy(() => import('./pages/Languages').then((module) => ({ default: module.Languages })));
const Contact = lazy(() => import('./pages/Contact').then((module) => ({ default: module.Contact })));
const About = lazy(() => import('./pages/About').then((module) => ({ default: module.About })));
const FAQ = lazy(() => import('./pages/FAQ').then((module) => ({ default: module.FAQ })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then((module) => ({ default: module.PrivacyPolicy })));
const Terms = lazy(() => import('./pages/Terms').then((module) => ({ default: module.Terms })));
const News = lazy(() => import('./pages/News').then((module) => ({ default: module.News })));
const Careers = lazy(() => import('./pages/Careers').then((module) => ({ default: module.Careers })));
const JobDetail = lazy(() => import('./pages/JobDetail').then((module) => ({ default: module.JobDetail })));
const DynamicPageLoader = lazy(() => import('./pages/DynamicPageLoader').then((module) => ({ default: module.DynamicPageLoader })));
const OrderStatus = lazy(() => import('./pages/OrderStatus'));
const OrderAccess = lazy(() => import('./pages/OrderAccess').then((module) => ({ default: module.OrderAccess })));
const TranslatorResponse = lazy(() => import('./pages/TranslatorResponse').then((module) => ({ default: module.TranslatorResponse })));
const TranslatorPortal = lazy(() => import('./pages/TranslatorPortal'));
const Login = lazy(() => import('./pages/admin/Login').then((module) => ({ default: module.Login })));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then((module) => ({ default: module.AdminLayout })));
const Dashboard = lazy(() => import('./pages/admin/Dashboard').then((module) => ({ default: module.Dashboard })));
const AdminOrders = lazy(() => import('./pages/admin/Orders').then((module) => ({ default: module.AdminOrders })));
const OrderDetail = lazy(() => import('./pages/admin/OrderDetail').then((module) => ({ default: module.OrderDetail })));
const AdminApplications = lazy(() => import('./pages/admin/AdminApplications').then((module) => ({ default: module.AdminApplications })));
const AdminFeedback = lazy(() => import('./pages/admin/AdminFeedback').then((module) => ({ default: module.AdminFeedback })));
const AdminContactMessages = lazy(() => import('./pages/admin/AdminContactMessages').then((module) => ({ default: module.AdminContactMessages })));
const AdminTranslators = lazy(() => import('./pages/admin/Translators').then((module) => ({ default: module.AdminTranslators })));
const AdminConfig = lazy(() => import('./pages/admin/AdminConfig').then((module) => ({ default: module.AdminConfig })));
const AdminServices = lazy(() => import('./pages/admin/Services').then((module) => ({ default: module.AdminServices })));
const AdminLanguages = lazy(() => import('./pages/admin/Languages').then((module) => ({ default: module.AdminLanguages })));
const AdminTranslations = lazy(() => import('./pages/admin/Translations').then((module) => ({ default: module.AdminTranslations })));
const AdminPages = lazy(() => import('./pages/admin/AdminPages').then((module) => ({ default: module.AdminPages })));
const AdminCareers = lazy(() => import('./pages/admin/AdminCareers').then((module) => ({ default: module.AdminCareers })));
const AdminEmailTemplates = lazy(() => import('./pages/admin/EmailTemplates').then((module) => ({ default: module.AdminEmailTemplates })));

const RouteLoadingFallback = () => <div className="min-h-[24vh]" aria-hidden="true" />;

const lazyElement = (Component: ComponentType<any>, props?: Record<string, unknown>) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <Component {...props} />
  </Suspense>
);

const EN_ROUTES = {
  services: STATIC_ROUTE_SEGMENTS.services.en,
  pricing: STATIC_ROUTE_SEGMENTS.pricing.en,
  languages: STATIC_ROUTE_SEGMENTS.languages.en,
  contact: STATIC_ROUTE_SEGMENTS.contact.en,
  about: STATIC_ROUTE_SEGMENTS.about.en,
  faq: STATIC_ROUTE_SEGMENTS.faq.en,
  privacy: STATIC_ROUTE_SEGMENTS.privacy.en,
  terms: STATIC_ROUTE_SEGMENTS.terms.en,
  news: STATIC_ROUTE_SEGMENTS.news.en,
  careers: STATIC_ROUTE_SEGMENTS.careers.en,
  careerAbout: CAREER_RESOURCE_SEGMENTS.about.en,
  careerValues: CAREER_RESOURCE_SEGMENTS.values.en,
  careerCulture: CAREER_RESOURCE_SEGMENTS.culture.en,
  careerInclusion: CAREER_RESOURCE_SEGMENTS.inclusion.en,
  careerEsg: CAREER_RESOURCE_SEGMENTS.esg.en,
  careerApply: CAREER_RESOURCE_SEGMENTS.apply.en,
  careerTips: CAREER_RESOURCE_SEGMENTS.tips.en,
  careerCompetencies: CAREER_RESOURCE_SEGMENTS.competencies.en,
  careerAccessibility: CAREER_RESOURCE_SEGMENTS.accessibility.en,
} as const;

const LT_ROUTES = {
  services: STATIC_ROUTE_SEGMENTS.services.lt,
  pricing: STATIC_ROUTE_SEGMENTS.pricing.lt,
  languages: STATIC_ROUTE_SEGMENTS.languages.lt,
  contact: STATIC_ROUTE_SEGMENTS.contact.lt,
  about: STATIC_ROUTE_SEGMENTS.about.lt,
  faq: STATIC_ROUTE_SEGMENTS.faq.lt,
  privacy: STATIC_ROUTE_SEGMENTS.privacy.lt,
  terms: STATIC_ROUTE_SEGMENTS.terms.lt,
  news: STATIC_ROUTE_SEGMENTS.news.lt,
  careers: STATIC_ROUTE_SEGMENTS.careers.lt,
  careerAbout: CAREER_RESOURCE_SEGMENTS.about.lt,
  careerValues: CAREER_RESOURCE_SEGMENTS.values.lt,
  careerCulture: CAREER_RESOURCE_SEGMENTS.culture.lt,
  careerInclusion: CAREER_RESOURCE_SEGMENTS.inclusion.lt,
  careerEsg: CAREER_RESOURCE_SEGMENTS.esg.lt,
  careerApply: CAREER_RESOURCE_SEGMENTS.apply.lt,
  careerTips: CAREER_RESOURCE_SEGMENTS.tips.lt,
  careerCompetencies: CAREER_RESOURCE_SEGMENTS.competencies.lt,
  careerAccessibility: CAREER_RESOURCE_SEGMENTS.accessibility.lt,
} as const;

const App = () => {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollManager />
        <ToastProvider>
          <AppProvider>
            <VerificationProvider>
            <Routes>
              {/* Standalone Pages */}
              <Route path="/translator/:token" element={lazyElement(TranslatorResponse)} />
              <Route path="/portal" element={lazyElement(TranslatorPortal)} />
              <Route path="/secure-access" element={lazyElement(OrderAccess)} />
              <Route path="/secure-access/:token" element={lazyElement(OrderAccess)} />

              {/* ENGLISH (Default) */}
              <Route path="/" element={<LanguageSync />}>
                <Route element={<MainLayout />}>
                  <Route index element={lazyElement(Home)} />
                  <Route path={EN_ROUTES.services} element={lazyElement(Services)} />
                  <Route path={`${EN_ROUTES.services}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={`${EN_ROUTES.pricing}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={EN_ROUTES.languages} element={lazyElement(Languages)} />
                  <Route path={EN_ROUTES.contact} element={lazyElement(Contact)} />
                  <Route path={EN_ROUTES.about} element={lazyElement(About)} />
                  <Route path={EN_ROUTES.faq} element={lazyElement(FAQ)} />
                  <Route path={EN_ROUTES.privacy} element={lazyElement(PrivacyPolicy)} />
                  <Route path={EN_ROUTES.terms} element={lazyElement(Terms)} />
                  <Route path={EN_ROUTES.news} element={lazyElement(News)} />
                  <Route path={`${EN_ROUTES.news}/:slug`} element={lazyElement(DynamicPageLoader)} />
                  <Route path={EN_ROUTES.careers} element={lazyElement(Careers)} />
                  
                  {/* Career Content Pages */}
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerAbout}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_about" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerValues}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_values" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerCulture}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_culture" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerInclusion}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_inclusion" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerEsg}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_esg" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerApply}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_apply" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerTips}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_tips" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerCompetencies}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_competencies" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerAccessibility}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_accessibility" })} />

                  <Route path={`${EN_ROUTES.careers}/:slug`} element={lazyElement(JobDetail)} />
                  <Route path="order-status" element={lazyElement(OrderStatus)} />

                  {/* Dynamic Catch-all for Pages */}
                  <Route path="*" element={lazyElement(DynamicPageLoader)} />
                </Route>
              </Route>

              {/* ENGLISH (Explicit) */}
              <Route path="/en" element={<LanguageSync />}>
                <Route element={<MainLayout />}>
                  <Route index element={lazyElement(Home)} />
                  <Route path={EN_ROUTES.services} element={lazyElement(Services)} />
                  <Route path={`${EN_ROUTES.services}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={`${EN_ROUTES.pricing}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={EN_ROUTES.languages} element={lazyElement(Languages)} />
                  <Route path={EN_ROUTES.contact} element={lazyElement(Contact)} />
                  <Route path={EN_ROUTES.about} element={lazyElement(About)} />
                  <Route path={EN_ROUTES.faq} element={lazyElement(FAQ)} />
                  <Route path={EN_ROUTES.privacy} element={lazyElement(PrivacyPolicy)} />
                  <Route path={EN_ROUTES.terms} element={lazyElement(Terms)} />
                  <Route path={EN_ROUTES.news} element={lazyElement(News)} />
                  <Route path={`${EN_ROUTES.news}/:slug`} element={lazyElement(DynamicPageLoader)} />
                  <Route path={EN_ROUTES.careers} element={lazyElement(Careers)} />
                  
                  {/* Career Content Pages */}
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerAbout}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_about" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerValues}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_values" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerCulture}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_culture" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerEsg}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_esg" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerApply}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_apply" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerTips}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_tips" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerCompetencies}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_competencies" })} />
                  <Route path={`${EN_ROUTES.careers}/${EN_ROUTES.careerAccessibility}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_accessibility" })} />

                  <Route path={`${EN_ROUTES.careers}/:slug`} element={lazyElement(JobDetail)} />
                  <Route path="order-status" element={lazyElement(OrderStatus)} />

                  {/* Dynamic Catch-all for Pages */}
                  <Route path="*" element={lazyElement(DynamicPageLoader)} />
                </Route>
              </Route>

              <Route path="/lt" element={<LanguageSync />}>
                <Route element={<MainLayout />}>
                  <Route index element={lazyElement(Home)} />
                  <Route path={LT_ROUTES.services} element={lazyElement(Services)} />
                  <Route path={`${LT_ROUTES.services}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={`${LT_ROUTES.pricing}/:slug`} element={lazyElement(ServiceDetail)} />
                  <Route path={LT_ROUTES.languages} element={lazyElement(Languages)} />
                  <Route path={LT_ROUTES.contact} element={lazyElement(Contact)} />
                  <Route path={LT_ROUTES.about} element={lazyElement(About)} />
                  <Route path={LT_ROUTES.faq} element={lazyElement(FAQ)} />
                  <Route path={LT_ROUTES.privacy} element={lazyElement(PrivacyPolicy)} />
                  <Route path={LT_ROUTES.terms} element={lazyElement(Terms)} />
                  <Route path={LT_ROUTES.news} element={lazyElement(News)} />
                  <Route path={`${LT_ROUTES.news}/:slug`} element={lazyElement(DynamicPageLoader)} />
                  <Route path={LT_ROUTES.careers} element={lazyElement(Careers)} />
                  
                  {/* Career Content Pages */}
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerAbout}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_about" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerValues}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_values" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerCulture}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_culture" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerInclusion}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_inclusion" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerEsg}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_esg" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerApply}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_apply" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerTips}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_tips" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerCompetencies}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_competencies" })} />
                  <Route path={`${LT_ROUTES.careers}/${LT_ROUTES.careerAccessibility}`} element={lazyElement(DynamicPageLoader, { pageKey: "career_accessibility" })} />

                  <Route path={`${LT_ROUTES.careers}/:slug`} element={lazyElement(JobDetail)} />
                  <Route path="order-status" element={lazyElement(OrderStatus)} />

                  {/* Dynamic Catch-all for Pages */}
                  <Route path="*" element={lazyElement(DynamicPageLoader)} />
                </Route>
              </Route>

              {/* ADMIN */}
              <Route
                path="/admin/login"
                element={<AdminTranslationBoundary>{lazyElement(Login)}</AdminTranslationBoundary>}
              />

              <Route
                path="/admin"
                element={(
                  <AdminTranslationBoundary>
                    <AdminSaveProvider>{lazyElement(AdminLayout)}</AdminSaveProvider>
                  </AdminTranslationBoundary>
                )}
              >
                <Route index element={lazyElement(Dashboard)} />
                <Route path="orders" element={lazyElement(AdminOrders)} />
                <Route path="orders/new" element={<Navigate to="/admin/orders" replace />} />
                <Route path="orders/:id" element={lazyElement(OrderDetail)} />
                <Route path="applications" element={lazyElement(AdminApplications)} />
                <Route path="feedback" element={lazyElement(AdminFeedback)} />
                <Route path="contact-messages" element={lazyElement(AdminContactMessages)} />
                <Route path="translator-approvals" element={<Navigate to="/admin/translators" replace />} />
                <Route path="translators" element={lazyElement(AdminTranslators)} />

                <Route path="config" element={lazyElement(AdminConfig)}>
                  <Route index element={<Navigate to="services" replace />} />
                  <Route path="services" element={lazyElement(AdminServices)} />
                  <Route path="languages" element={lazyElement(AdminLanguages)} />
                  <Route path="website-text" element={lazyElement(AdminTranslations)} />
                  <Route path="pages" element={lazyElement(AdminPages)} />
                  <Route path="media" element={lazyElement(AdminPages)} />
                  <Route path="pages/new" element={lazyElement(AdminPages)} />
                  <Route path="pages/:pageId" element={lazyElement(AdminPages)} />
                  <Route path="pages/careers" element={lazyElement(AdminCareers)} />
                  <Route path="emails" element={lazyElement(AdminEmailTemplates)} />
                  <Route path="contact-messages" element={<Navigate to="/admin/contact-messages" replace />} />
                </Route>

                <Route path="pages" element={<Navigate to="/admin/config/pages" replace />} />
                <Route path="services" element={<Navigate to="/admin/config/services" replace />} />
                <Route path="languages" element={<Navigate to="/admin/config/languages" replace />} />
                <Route path="translations" element={<Navigate to="/admin/config/website-text" replace />} />
                <Route path="emails" element={<Navigate to="/admin/config/emails" replace />} />
                <Route path="careers" element={<Navigate to="/admin/config/pages/careers" replace />} />
              </Route>

              {}
              <Route path="*" element={<Navigate to="/lt" replace />} />
            </Routes>
            </VerificationProvider>
          </AppProvider>
        </ToastProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;


